import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PlanDetails } from 'planning-portal-library/src/lib/core/domain/models/plan-details';
import { PlanJobDetails } from 'planning-portal-library/src/lib/core/domain/models/plan-job-details';
import { DeleteCompletePlanReques } from 'planning-portal-library/src/lib/core/domain/requests/plans/delete-complete-plan';
import { PlanDetailsRequest } from 'planning-portal-library/src/lib/core/domain/requests/plans/plan-details-request';
import { ValidateJLCsvFileResponse } from 'planning-portal-library/src/lib/core/domain/requests/plans/ValidateJLCsvFileResponse';
import { injectEnvironment } from 'planning-portal-library/src/lib/core/environments/environment.token';
import {
  catchError,
  forkJoin,
  map,
  mergeMap,
  Observable,
  reduce,
  share,
} from 'rxjs';
import {
  AttachScheduler,
  AttachSchedulerRequest,
  BindLocationWithMultiplePlansJobRequest,
  CalculatePlanETARequest,
  ClonePlanRequest,
  GenerateRoutesRequest,
  GetPlansRequest,
  ImportPlanRequest,
  InjectPlanLocationRequest,
  OptimizePlanRequest,
  Plan,
  PlanRequest,
  PlanRevisionRequest,
  PlanWithJobRequest,
  PlanWithLocationRequest,
  UnCommitPlanRequest,
} from '../../../../core/domain';
import { PlansRepository } from '../../../../core/repositories';
import { BaseGridResponse } from '../../../shared/base-grid.response';
import { BaseRequest } from '../../../shared/base.request';
import { BaseResponse } from '../../../shared/base.response';
import { isBlobError, parseErrorBlob } from '../../../shared/helpers';
import { LocationsApiUrls } from '../../locations/locations-web-repository/location-api-urls';
import { PlanDetailWebEntity } from './entities/plan-detail-web-entity';
import { PlanDetailWebRepositoryMapper } from './entities/plan-detail-web-repository-mapper';
import { PlanJobDetailWebEntity } from './entities/plan-job-detail-web-entity';
import { PlanJobDetailWebRepositoryMapper } from './entities/plan-job-detail-web-repository-mapper';
import { PlanWebEntity } from './entities/plan-web-entity';
import { PlanWebRepositoryMapper } from './entities/plan-web-repository-mapper';
import { SchedulerWebEntity } from './entities/scheduler-web-entity';
import { SchedulerWebRepository } from './entities/scheduler-web-repository-mapper';
import { PlanApiUrls } from './plan-api-urls';
import { AssignLocationsGroupsToPlanRequest } from 'planning-portal-library/src/lib/core/domain/requests/plans/assign-location-to-plan-request';
import { createCompleteHDPlanRequest  } from 'planning-portal-library/src/lib/core/domain/requests/plans/remote-plan-request';

@Injectable({
  providedIn: 'root' 
})
export class PlanWebRepository extends PlansRepository {
  private readonly planMapper = new PlanWebRepositoryMapper();
  private readonly schedulerWebRepositoryMapper = new SchedulerWebRepository();
  private readonly planDetailWebRepositoryMapper = new PlanDetailWebRepositoryMapper();
  private readonly planJobDetailWebRepositoryMapper =
    new PlanJobDetailWebRepositoryMapper();
  plans: PlanWebEntity[] = [];
  private readonly environment = injectEnvironment();

  constructor(private readonly http: HttpClient) {
    super();
  }

  getPlans(
    request: BaseRequest<GetPlansRequest>
  ): Observable<BaseGridResponse<Plan>> {
    const httpMap = this.http
      .post<BaseResponse<BaseGridResponse<PlanWebEntity>>>(
        `${this.environment.url}${PlanApiUrls.GetPlans}`,
        request.request
      )
      .pipe(share());

    const dataResultMap = httpMap
      .pipe(
        mergeMap((response) => response.result!.data),
        catchError((err) => {
          throw new err();
        })
      )
      .pipe(map(this.planMapper.mapFrom))
      .pipe(reduce((items: Plan[], item: Plan) => [...items, item], []));

    const totalRecordsResultMap = httpMap.pipe(
      map((response) => response.result!.totalRecords)
    );

    return forkJoin([dataResultMap, totalRecordsResultMap]).pipe(
      map((input) => {
        const item: BaseGridResponse<Plan> = {
          data: input[0],
          totalRecords: +input[1],
        };
        return item;
      })
    );
  }
  bindLocationWithMultiplePlanJob(
    request: BindLocationWithMultiplePlansJobRequest
  ): Observable<boolean> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.BindLocationWithMultiplePlanJob}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  addPlan(request: PlanRequest): Observable<string> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.CreatePlan}`,
        request
      )
      .pipe(
        map((data: BaseResponse<string>) => {
          return data.result!;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  addPlanWithLocation(request: PlanWithLocationRequest): Observable<string> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.CreatePlanWithLocations}`,
        request
      )
      .pipe(
        map((data) => {
          return data.result!;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  attachScheduler(request: AttachSchedulerRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${PlanApiUrls.CreateEditPlanSchedule}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  editPlan(request: PlanRequest): Observable<string> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.EditPlan}`,
        request
      )
      .pipe(
        map((data) => {
          return data.result!;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  
  assignLocationsGroupsToPlan(request: AssignLocationsGroupsToPlanRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${PlanApiUrls.AssignLocationsGroupsToPlan}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful!;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  deletePlan(request: DeleteCompletePlanReques): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${PlanApiUrls.DeletePlan}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  clonePlan(request: ClonePlanRequest): Observable<Plan> {
    return this.http
      .post<BaseResponse<PlanWebEntity>>(
        `${this.environment.url}${PlanApiUrls.ClonePlan}`,
        request
      )
      .pipe(
        map((data) => {
          return data.result!;
        }),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.planMapper.mapFrom));
  }

  optimizePlan(request: OptimizePlanRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.OptimizePlan}`,
        request
      )
      .pipe(
        map((response: any) => {
          return response.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  calculatePlanETA(request: CalculatePlanETARequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.CalculatePlanETA}`,
        request
      )
      .pipe(
        map((response: any) => {
          return response.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
  calculateHDPlanETA(request: CalculatePlanETARequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.CalculateHDPlanETA}`,
        request
      )
      .pipe(
        map((response: any) => {
          return response.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  addPlanWithJob(request: PlanWithJobRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<unknown>>(
        `${this.environment.url}${PlanApiUrls.CreatePlanJobs}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  editPlanWithJob(request: PlanWithJobRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.EditPlanJobs}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  generateRoutes(request: GenerateRoutesRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.GenerateRoutes}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  unCommitPlan(request: UnCommitPlanRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.UnCommitPlan}`,
        request
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  removePlanScheduler(request: string): Observable<boolean> {
    return this.http
      .delete<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.RemovePlanScheduler}?id=${request}`
      )
      .pipe(
        map((data) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  getScheduler(request: string): Observable<AttachScheduler> {
    return this.http
      .get<BaseResponse<SchedulerWebEntity>>(
        `${this.environment.url}${PlanApiUrls.GetPlanSchedule}` + request
      )
      .pipe(
        map((response) => {
          return response.result!;
        }),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.schedulerWebRepositoryMapper.mapFrom));
  }

  planRevision(request: PlanRevisionRequest): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${PlanApiUrls.CreatePlanRevision}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  importPlan(request?: ImportPlanRequest | undefined): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${PlanApiUrls.ImportPlan}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }

  importPlanCSV(request: FormData): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${PlanApiUrls.ImportPlanCSV}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
  validateJobsCSV(request: FormData): Observable<ValidateJLCsvFileResponse> {
    return this.http
      .post<BaseResponse<ValidateJLCsvFileResponse>>(
        `${this.environment.url}${PlanApiUrls.ValidateJobsCSV}`,
        request
      )
      .pipe(
        map((data) => {
          return data.result!;
        }),
        catchError((err) => {
          throw err;
        })
      );
    // .pipe(map(this.jobMapper.mapFrom));
  }

  importJobsCSV(request: ValidateJLCsvFileResponse): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${PlanApiUrls.UpdatePlanByValidatedDataOfCSV}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
  exportPlanCSV(request: BaseRequest<string>): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set('id', request.request);
    return this.http
      .get(`${this.environment.url}${PlanApiUrls.ExportPlanCSV}`, {
        params,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError((err) => {
          if (isBlobError(err)) {
            return parseErrorBlob(err);
          }
          throw err;
        })
      );
  }

  exportPlanToCSV(request: BaseRequest<string>): Observable<any> {
    const params = new HttpParams().set('id', request.request);
    return this.http
      .get(`${this.environment.url}${PlanApiUrls.ExportPlanToCsv}`, {
        params,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError((err) => {
          if (isBlobError(err)) {
            return parseErrorBlob(err);
          }
          throw err;
        })
      );
  }

  getAllPlanJobs(
    request: BaseRequest<PlanDetailsRequest>
  ): Observable<PlanDetails[]> {
    return this.http
      .post<BaseResponse<PlanDetailWebEntity[]>>(
        `${this.environment.url}${PlanApiUrls.GetAllPlanJobs}`,
        request.request
      )
      .pipe(
        mergeMap((data) => {
          return data.result!;
        }),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.planDetailWebRepositoryMapper.mapFrom))
      .pipe(
        reduce(
          (items: PlanDetails[], item: PlanDetails) => [...items, item],
          []
        )
      );
  }
  getPlanJobDetails(
    request: BaseRequest<PlanDetailsRequest>
  ): Observable<PlanJobDetails> {
    return this.http
      .post<BaseResponse<PlanJobDetailWebEntity>>(
        `${this.environment.url}${PlanApiUrls.GetAllPlanJobs}`,
        request.request
      )
      .pipe(
        map((data) => {
          return data.result!;
        }),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.planJobDetailWebRepositoryMapper.mapFrom));
  }

  getPlanById(request: string): Observable<Plan> {
    const httpMap = this.http.get<BaseResponse<PlanWebEntity>>(
      `${this.environment.url}${PlanApiUrls.GetPlanById}`,
      {
        params: {
          id: request,
        },
      }
    );
    return httpMap
      .pipe(
        map((response: BaseResponse<PlanWebEntity>) => response.result!),
        catchError((err) => {
          throw err;
        })
      )
      .pipe(map(this.planMapper.mapFrom));
  }

  injectPlanLocations(request: InjectPlanLocationRequest): Observable<string> {
    return this.http
      .post<BaseResponse<string>>(
        `${this.environment.url}${PlanApiUrls.InjectPlanLocations}`,
        request
      )
      .pipe(
        map((data) => {
          return data.result!;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
  importPlanLocationCSV(request: FormData): Observable<boolean> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${LocationsApiUrls.ImportPlanLocationCsv}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data.successful;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
    addPlanFromAms(request: createCompleteHDPlanRequest): Observable<BaseResponse<boolean>> {
    return this.http
      .post<BaseResponse<boolean>>(
        `${this.environment.url}${PlanApiUrls.CreatePlanFromAms}`,
        request
      )
      .pipe(
        map((data: BaseResponse<boolean>) => {
          return data;
        }),
        catchError((err) => {
          throw err;
        })
      );
  }
}
